import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import {
  IWeeklyRewardAllocations,
  useWeeklyRewardAllocations,
} from "../../hooks/api/admin/useWeeklyRewardAllocations";
import { useCryptoMajorsInstruments } from "../../hooks/api/admin/useCryptoMajorsInstruments";
import { CryptoMajorsInstruments } from "./CryptoMajorsInstruments";
import { NerfedMMVolumeWeightParams } from "./NerfedMMVolumeWeightParams";
import { RewardVolumeAllocations } from "./RewardVolumeAllocations";
import { IRewardVolumeAllocationRule } from "../../hooks/api/admin/useRewardVolumeAllocations";
import {
  Actions,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  ErrorText,
  Grid,
  Input,
  Label,
  LabelHint,
  PageWrapper,
  SectionNav,
  SectionNavLink,
  Summary,
  SummaryLabel,
  SummaryValue,
  ToolPanel,
  WorkspaceHeader,
} from "./style";

const TOTAL_POOL_AEVO = 1_000_000;

type Form = Record<keyof IWeeklyRewardAllocations, string>;
type RewardTool = "pool" | "instruments" | "mm-weight" | "volume-routing";

interface IWeeklyRewardAllocationsProps {
  initialTool?: RewardTool;
  previewRules?: IRewardVolumeAllocationRule[];
}

const rewardTools: { key: RewardTool; label: string }[] = [
  { key: "pool", label: "Pool allocation" },
  { key: "instruments", label: "Major instruments" },
  { key: "mm-weight", label: "MM volume weight" },
  { key: "volume-routing", label: "Volume routing" },
];

// Crypto Majors hint pulls the live list from the backend so it never
// drifts after admins edit it via the Crypto Majors Instruments card.
const buildCategories = (
  cryptoMajorsInstruments: string[]
): {
  key: keyof IWeeklyRewardAllocations;
  label: string;
  hint: string;
}[] => {
  const majorsHint = cryptoMajorsInstruments.length
    ? `${cryptoMajorsInstruments.join(" / ")} perp volume`
    : "curated perp volume (no instruments set)";
  return [
    {
      key: "crypto_majors",
      label: "Crypto Majors",
      hint: majorsHint,
    },
    {
      key: "options",
      label: "Options",
      hint: "delta-adjusted options notional",
    },
    {
      key: "crypto",
      label: "Crypto (all)",
      hint: "every non-RWA perp — overlaps with Crypto Majors, set one or the other",
    },
    {
      key: "equity",
      label: "Equity",
      hint: "AAPL / GOOGL / equity perp volume",
    },
    {
      key: "commodity",
      label: "Commodity",
      hint: "XAU / XAG / commodity perp volume",
    },
    { key: "rwa", label: "RWA (combined)", hint: "equity + commodity + FX" },
  ];
};

const emptyForm: Form = {
  crypto: "",
  options: "",
  equity: "",
  commodity: "",
  rwa: "",
  crypto_majors: "",
};

const parseValue = (raw: string): number => {
  if (raw.trim() === "") return 0;
  const n = Number(raw.replace(/,/g, ""));
  return Number.isFinite(n) ? n : NaN;
};

const fmt = (n: number): string => n.toLocaleString();

export function WeeklyRewardAllocations({
  initialTool = "pool",
  previewRules,
}: IWeeklyRewardAllocationsProps = {}) {
  const { data, isLoading, updateWeeklyRewardAllocations } =
    useWeeklyRewardAllocations();
  const { data: instrumentsData } = useCryptoMajorsInstruments();

  const categories = useMemo(
    () => buildCategories(instrumentsData?.instruments ?? []),
    [instrumentsData]
  );

  const [form, setForm] = useState<Form>(emptyForm);
  const [activeTool, setActiveTool] = useState<RewardTool>(initialTool);

  // Reset form whenever fresh data arrives from the server.
  useEffect(() => {
    if (!data) return;
    setForm({
      crypto: String(data.crypto),
      options: String(data.options),
      equity: String(data.equity),
      commodity: String(data.commodity),
      rwa: String(data.rwa),
      crypto_majors: String(data.crypto_majors),
    });
  }, [data]);

  const parsed = useMemo(
    () => ({
      crypto: parseValue(form.crypto),
      options: parseValue(form.options),
      equity: parseValue(form.equity),
      commodity: parseValue(form.commodity),
      rwa: parseValue(form.rwa),
      crypto_majors: parseValue(form.crypto_majors),
    }),
    [form]
  );

  const total = useMemo(
    () =>
      parsed.crypto +
      parsed.options +
      parsed.equity +
      parsed.commodity +
      parsed.rwa +
      parsed.crypto_majors,
    [parsed]
  );

  const anyInvalid = useMemo(
    () =>
      Object.values(parsed).some((v) => !Number.isFinite(v) || v < 0) ||
      Object.values(parsed).some((v) => !Number.isInteger(v)),
    [parsed]
  );

  const sumOk = total === TOTAL_POOL_AEVO;
  const dirty = useMemo(() => {
    if (!data) return false;
    return (
      parsed.crypto !== data.crypto ||
      parsed.options !== data.options ||
      parsed.equity !== data.equity ||
      parsed.commodity !== data.commodity ||
      parsed.rwa !== data.rwa ||
      parsed.crypto_majors !== data.crypto_majors
    );
  }, [data, parsed]);

  const onChange = useCallback(
    (key: keyof IWeeklyRewardAllocations) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        // Allow only digits + optional thousands commas.
        if (v !== "" && !/^\d{1,3}(,\d{3})*$|^\d+$/.test(v)) return;
        setForm((prev) => ({ ...prev, [key]: v }));
      },
    []
  );

  const onSave = useCallback(async () => {
    if (anyInvalid || !sumOk) return;
    const confirmed = window.confirm(
      `Update weekly reward allocations?\n\n${categories
        .map((c) => `${c.label}: ${fmt(parsed[c.key])} AEVO`)
        .join("\n")}\nTotal: ${fmt(total)} AEVO\n\n` +
        "This takes effect immediately. Leaderboards for categories set to 0 will be hidden from the public endpoint."
    );
    if (!confirmed) return;
    await updateWeeklyRewardAllocations(parsed);
  }, [
    anyInvalid,
    categories,
    parsed,
    sumOk,
    total,
    updateWeeklyRewardAllocations,
  ]);

  const onReset = useCallback(() => {
    if (!data) return;
    setForm({
      crypto: String(data.crypto),
      options: String(data.options),
      equity: String(data.equity),
      commodity: String(data.commodity),
      rwa: String(data.rwa),
      crypto_majors: String(data.crypto_majors),
    });
  }, [data]);

  return (
    <PageWrapper>
      <Card>
        <WorkspaceHeader>
          <SectionNav role="tablist" aria-label="Weekly rewards tools">
            {rewardTools.map((tool) => (
              <SectionNavLink
                key={tool.key}
                type="button"
                role="tab"
                aria-selected={activeTool === tool.key}
                isActive={activeTool === tool.key}
                onClick={() => setActiveTool(tool.key)}
              >
                {tool.label}
              </SectionNavLink>
            ))}
          </SectionNav>
        </WorkspaceHeader>

        {activeTool === "pool" && (
          <ToolPanel role="tabpanel">
            <CardHeader>
              <CardTitle>Weekly Reward Allocations</CardTitle>
              <CardSubtitle>
                Split the weekly {fmt(TOTAL_POOL_AEVO)} AEVO pool across the six
                leaderboard categories. Values must be whole AEVO and sum to{" "}
                {fmt(TOTAL_POOL_AEVO)}. Categories set to 0 are hidden from the
                public weekly-leaderboard endpoint. Takes effect immediately.{" "}
                <strong>Crypto Majors</strong> overlaps with{" "}
                <strong>Crypto (all)</strong> — funding both will double-count
                those volumes, so generally pick one or the other.
              </CardSubtitle>
            </CardHeader>

            <Grid>
              {categories.map((c) => {
                const v = parsed[c.key];
                const invalid =
                  !Number.isFinite(v) || v < 0 || !Number.isInteger(v);
                return (
                  <Fragment key={c.key}>
                    <Label htmlFor={c.key}>
                      {c.label}
                      <LabelHint>{c.hint}</LabelHint>
                    </Label>
                    <div>
                      <Input
                        id={c.key}
                        hasErrors={invalid}
                        value={form[c.key]}
                        onChange={onChange(c.key)}
                        placeholder={data ? fmt(data[c.key]) : "0"}
                        inputMode="numeric"
                        disabled={isLoading}
                      />
                      {invalid && (
                        <ErrorText>
                          Must be a non-negative whole number.
                        </ErrorText>
                      )}
                    </div>
                  </Fragment>
                );
              })}
            </Grid>

            <Summary>
              <SummaryLabel>
                Total ({fmt(total)} / {fmt(TOTAL_POOL_AEVO)} AEVO)
              </SummaryLabel>
              <SummaryValue valid={sumOk}>
                {(() => {
                  if (sumOk) return "OK";
                  const diff = total - TOTAL_POOL_AEVO;
                  return diff > 0 ? `+${fmt(diff)}` : `-${fmt(-diff)}`;
                })()}
              </SummaryValue>
            </Summary>

            <Actions>
              <Button
                buttonTheme={ButtonThemeEnum.NEUTRAL2}
                onClick={onReset}
                disabled={isLoading || !dirty}
              >
                Reset
              </Button>
              <Button
                buttonTheme={ButtonThemeEnum.PRIMARY}
                onClick={onSave}
                disabled={isLoading || anyInvalid || !sumOk || !dirty}
              >
                Save
              </Button>
            </Actions>
          </ToolPanel>
        )}

        {activeTool === "instruments" && <CryptoMajorsInstruments />}

        {activeTool === "mm-weight" && <NerfedMMVolumeWeightParams />}

        {activeTool === "volume-routing" && (
          <RewardVolumeAllocations previewRules={previewRules} />
        )}
      </Card>
    </PageWrapper>
  );
}
